import React from 'react';

import PropTypes from 'prop-types';

import Avatar from '@adsk/alloy-react-avatar';
import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { User, userShape } from '../consts';

type ActivityUserProps = StylableComponent<HTMLDivElement> & {
  user: User;
  isRemoved?: boolean;
};

/**
 * ActivityUser component. Describes a single user, containing avatar and name.
 */
const ActivityUser = React.forwardRef<HTMLDivElement, ActivityUserProps>(
  ({ user, isRemoved, style, className, ...props }, ref) => {
    const activityLogTokens = useTheme().tokens?.component?.activityLog;
    const userTokens = activityLogTokens?.user;

    return (
      <div
        ref={ref}
        className={className}
        css={[
          {
            display: 'flex',
            alignItems: 'center',
          },
          style,
        ]}
        {...props}
      >
        <Avatar
          size={Avatar.SIZES.SMALL_16}
          image={user.imageUrl ?? undefined}
          name={user.name}
          style={{
            alignSelf: 'flex-start',
            marginTop: userTokens?.['avatar-margin-top'],
          }}
        />
        <OverflowTooltip
          singleLine={false}
          lineClamp={2}
          getTargetProps={() => ({
            style: {
              fontFamily: userTokens?.fontFamily,
              fontSize: userTokens?.fontSize,
              fontWeight: userTokens?.fontWeight,
              lineHeight: userTokens?.lineHeight,
              color: isRemoved
                ? userTokens?.['removed-color']
                : userTokens?.color,
              textDecoration: isRemoved ? 'line-through' : 'none',
              marginLeft: userTokens?.marginLeft,
              wordBreak: 'break-word',
            },
          })}
        >
          {user.name}
        </OverflowTooltip>
      </div>
    );
  },
);

ActivityUser.displayName = 'ActivityUser';

ActivityUser.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The user to show */
  user: userShape.isRequired,
  /** Whether or not the user was removed */
  isRemoved: PropTypes.bool,
};

export default ActivityUser;
